import { EXPLORE_BASE_ROUTE, Routes } from '~/modules/routes';
import { FilterParams, SearchParams } from '../stores/searchStore';

interface FilterExtraParam {
    key: string;
    value: string[];
}

interface ExtraUrlParams {
    sort?: string;
    type?: string;
    filter?: FilterExtraParam;
    resetState?: boolean;
    expand?: string[] | string | undefined;
}

type UrlParam = { name: string; value: string };

const arrayNotEmptyNotEmptyValue = (value: string[]): boolean => {
    if (!value || value.length === 0) {
        return false;
    }
    for (const entry of value) {
        if (entry) {
            return true;
        }
    }
    return false;
};

const buildFilterParams = (filterParams: FilterParams, filter?: FilterExtraParam): UrlParam[] => {
    return Object.entries(filterParams).reduce((filters, [key, value]) => {
        if (key === filter?.key && arrayNotEmptyNotEmptyValue(filter?.value)) {
            return [...filters, { name: key, value: encodeURIComponent(filter.value.join(';')) }];
        }
        if (key !== filter?.key) {
            if (value) {
                const valueAsString = Array.isArray(value) ? value.join(';') : value;
                return [...filters, { name: key, value: encodeURIComponent(valueAsString) }];
            }
        }
        return filters;
    }, [] as UrlParam[]);
};

const buildExpandParam = (expand: string | string[]): UrlParam => {
    return {
        name: 'expand',
        value: encodeURIComponent(Array.isArray(expand) ? expand.join(',') : expand),
    };
};

// @ts-expect-error * Automated Type Error Fix
const buildSortParam = (sortOption): UrlParam => {
    // @ts-expect-error * Automated Type Error Fix
    return sortOption && sortOption !== 'relevant' ? { name: 'sort', value: sortOption } : null;
};

const buildTypeParam = (type: string): UrlParam => {
    if (!type || type === 'apps') {
        // @ts-expect-error * Automated Type Error Fix
        return null;
    }
    return { name: 'type', value: type };
};

const buildKeywordParam = (encodedKeyword?: string) => {
    return encodedKeyword ? { name: 'keywords', value: encodedKeyword } : null;
};

const buildUrl = (baseUrl: string, params: UrlParam[]) => {
    const validParams = params.filter(Boolean);
    if (validParams.length > 0) {
        const paramString = validParams.map((param) => `${param.name}=${param.value}`).join('&');
        return `${baseUrl}?${paramString}`;
    }
    return baseUrl;
};

const buildGenericNavigationUrl = (
    baseUrl: string,
    encodedKeyword?: string,
    sortParam?: string,
    filterParams?: FilterParams,
    {
        sort = undefined,
        type = undefined,
        filter = undefined,
        resetState = false,
        expand = undefined,
    }: ExtraUrlParams = {},
): string => {
    const sortBy = sort || sortParam;
    // @ts-expect-error * Automated Type Error Fix
    const baseParams: UrlParam[] = [buildKeywordParam(encodedKeyword), buildTypeParam(type)];

    if (resetState) {
        return buildUrl(baseUrl, baseParams);
    }

    const completeParams = [...baseParams, buildSortParam(sortBy), ...buildFilterParams(filterParams || {}, filter)];

    if (expand) {
        completeParams.push(buildExpandParam(expand));
    }

    return buildUrl(baseUrl, completeParams);
};

const buildNavigationUrl = (
    searchParams: SearchParams,
    {
        sort = undefined,
        type = undefined,
        filter = undefined,
        resetState = false,
        expand = undefined,
    }: ExtraUrlParams = {},
): string => {
    const encodedKeyword = searchParams.keyword ? encodeURIComponent(searchParams.keyword) : undefined;
    return buildGenericNavigationUrl(
        `/${Routes.SEARCH}`,
        encodedKeyword,
        searchParams.sort,
        searchParams.filterParams,
        { sort, type, filter, resetState, expand },
    );
};

const buildExploreNavigationUrl = (
    bucket: string,
    searchParams: SearchParams,
    {
        sort,
        type,
        filter,
        resetState,
        advancedFilters,
    }: {
        sort?: string;
        type?: string;
        filter?: { key: string; value: string[] };
        resetState?: boolean;
        advancedFilters?: boolean;
    } = {},
): string => {
    const advancedFiltersParam = advancedFilters ? '&advancedFilters=true' : '';
    return (
        buildGenericNavigationUrl(`/${EXPLORE_BASE_ROUTE}/${bucket}`, undefined, undefined, searchParams.filterParams, {
            sort,
            type,
            filter,
            resetState,
        }) + advancedFiltersParam
    );
};

export { buildExploreNavigationUrl, buildNavigationUrl };
